import clsx from 'clsx';
import type { ComponentProps, FunctionComponent } from 'react';

import { CheckIcon } from '../icons';
import { PopoverContent } from '../popover/Popover';

export const StyledPopoverContent: FunctionComponent<ComponentProps<typeof PopoverContent>> = ({
    className,
    ...props
}) => (
    <PopoverContent
        className={clsx(
            '!bg-surface-hover !border !border-border-subtle !rounded-xl !shadow-md !min-w-[232px] !p-xs font-sans',
            'w-[var(--radix-popover-trigger-width)]',
            className,
        )}
        {...props}
    />
);

export const Wrapper: FunctionComponent<ComponentProps<'div'>> = ({ className, ...props }) => (
    <div className={clsx('flex flex-col gap-s h-full overflow-hidden', className)} {...props} />
);

export const List: FunctionComponent<ComponentProps<'div'> & { readonly maxHeight?: string }> = ({
    className,
    maxHeight = '30vh',
    style,
    ...props
}) => (
    <div className={clsx('flex flex-auto flex-col gap-[4px]', className)} style={{ maxHeight, ...style }} {...props} />
);

export const Empty: FunctionComponent<ComponentProps<'div'>> = ({ className, ...props }) => (
    <div className={clsx('text-text-disabled text-sm leading-[1.25rem] text-center px-m py-m', className)} {...props} />
);

export const StyledItem: FunctionComponent<ComponentProps<'div'> & { readonly disabled?: boolean }> = ({
    className,
    disabled = false,
    ...props
}) => (
    <div
        className={clsx(
            'flex min-w-0 items-center relative gap-m rounded-md select-none',
            'text-body-xxs font-normal px-xl py-s',
            'transition-[background-color] duration-200 ease-in-out',
            disabled
                ? 'text-text-content-faint cursor-default opacity-50 pointer-events-none'
                : 'text-text-content-strong cursor-pointer hover:bg-surface-subtle',
            'aria-selected:bg-surface-subtle aria-selected:text-text-form',
            'data-[active=true]:bg-surface-subtle',
            className,
        )}
        {...props}
    />
);

export const IconWrapper: FunctionComponent<ComponentProps<'div'>> = ({ className, ...props }) => (
    <div className={clsx('flex items-center justify-center shrink-0 h-[16px] w-[16px]', className)} {...props} />
);

export const ItemContent: FunctionComponent<ComponentProps<'div'>> = ({ className, ...props }) => (
    <div className={clsx('flex min-w-0 items-center flex-1 gap-[8px]', className)} {...props} />
);

export const ItemLabel: FunctionComponent<ComponentProps<'span'>> = ({ className, ...props }) => (
    <span className={clsx('min-w-0 flex-1 truncate', className)} {...props} />
);

export const StyledCheckIcon: FunctionComponent<ComponentProps<typeof CheckIcon> & { readonly $color?: string }> = ({
    $color,
    className,
    style,
    ...props
}) => (
    <CheckIcon
        className={clsx('shrink-0', className)}
        style={{ color: $color ?? 'var(--semantic-accent)', ...style }}
        {...props}
    />
);
